/**
 * QMS permission keys — runtime registry + role/profile card list.
 * Role and user profile QMS card shows ONLY QMS_ROLE_PROFILE_PERMISSIONS.
 */

/** Screens/routes used at runtime (not all appear on role/profile card). */
export const QMS_PAGE_PERMISSIONS = [
  { key: "QMS_DASHBOARD", label: "Compliance Dashboard" },
  { key: "QMS_COMPLIANCE_REPORT", label: "Compliance Report" },
  { key: "QMS_DOCUMENT_TYPE", label: "Document Type" },
  { key: "QMS_DOCUMENT_CATEGORY", label: "Document Category" },
  { key: "QMS_VAULT", label: "My Vault" },
  { key: "QMS_PUBLISH_DOCUMENTS", label: "Publish Document" },
  { key: "QMS_DOCUMENT_LIBRARY", label: "Document Library" },
  { key: "QMS_CREATE_CHANGE_REQUEST", label: "Create Change Request" },
  { key: "QMS_CHANGE_REQUESTS", label: "All Change Requests" },
  { key: "QMS_TRACKER", label: "QMS Tracker" },
  { key: "QMS_TRANSFER_OWNERSHIP", label: "Transfer Ownership" },
  { key: "QMS_READING_LIST", label: "Reading List" },
  { key: "QMS_ARCHIVE_DOCUMENTS", label: "Archive Documents" },
  { key: "QMS_CERTIFICATE_TEMPLATE", label: "Certificate Template" },
  { key: "QMS_QUESTION_BANK", label: "Question Bank" },
  { key: "QMS_QUIZ_MANAGER", label: "Quiz Manager" },
  { key: "QMS_MY_LEARNING", label: "My Learning" },
  { key: "QMS_MY_RECORDS", label: "My Records" },
  { key: "QMS_SHARED_WITH_ME", label: "Shared With Me" },
  { key: "QMS_DOCUMENT_CONTROL", label: "Document Control" },
];

/** Workflow / library actions (runtime). */
export const QMS_ACTION_PERMISSIONS = [
  {
    key: "QMS_VIEW_CHANGE_REQUEST",
    label: "View Change Request",
    group: "Change Request",
    legacyActionName: "View Change Request",
    legacyActionTargetView: "DMS_CHANGEREQUESTS",
  },
  {
    key: "QMS_CHANGE_STATUS",
    label: "Change Status (Approve/Reject)",
    group: "Change Request",
    legacyActionName: "Change Status (Approve/Reject)",
  },
  {
    key: "QMS_CAN_REVIEW_DOCUMENTS",
    label: "Review Document",
    group: "Workflow",
    legacyActionName: "Can Review Documents",
  },
  {
    key: "QMS_CAN_APPROVE_DOCUMENTS",
    label: "Approve Document",
    group: "Workflow",
    legacyActionName: "Can Approve Documents",
  },
  {
    key: "QMS_LIBRARY_SEE_STAFF",
    label: "Assign/Unassign Library Staff",
    group: "Workflow",
    legacyActionName: "Can see library staff",
  },
  {
    key: "QMS_LIBRARY_ADD_STAFF",
    label: "Assign/Unassign My Learning Staff",
    group: "Workflow",
    legacyActionName: "Add Staff",
  },
  {
    key: "QMS_LIBRARY_VIEW_ASSIGNED_STAFF",
    label: "View Assigned Staffs",
    group: "Document Library",
    legacyActionName: "View Assigned Staffs",
  },
  {
    key: "QMS_LIBRARY_AUDIT_SUMMARY",
    label: "Audit Summary",
    group: "Document Library",
    legacyActionName: "Audit Summary",
    legacyActionTargetView: "DMS_DOCUMENTLIBRARY_AUDITSUMMARY",
  },
];

/**
 * Legacy granular keys kept for runtime / stored-role compatibility.
 * Not shown on the role/profile card — consolidated under parent permissions.
 */
export const QMS_GRANULAR_PERMISSIONS = [
  { key: "QMS_QUIZ_ADD_EDIT", label: "Add/Edit Quiz" },
  { key: "QMS_QUIZ_DELETE", label: "Delete Quiz" },
  { key: "QMS_QUESTION_ADD_EDIT", label: "Add/Edit Question" },
  { key: "QMS_QUESTION_DELETE", label: "Delete Question" },
  { key: "QMS_QUESTION_IMPORT_BULK", label: "Import Questions In bulk" },
  { key: "QMS_UNARCHIVE_DOCUMENTS", label: "Unarchive Document" },
];

/**
 * Parent permission → child keys it fully grants (view + all actions).
 * Used for role-card consolidation and runtime checks.
 */
export const QMS_CONSOLIDATED_PARENT_GRANTS = {
  QMS_QUIZ_MANAGER: [
    "QMS_QUIZ_ADD_EDIT",
    "QMS_QUIZ_DELETE",
    "QMS_QUESTION_ADD_EDIT",
    "QMS_QUESTION_DELETE",
    "QMS_QUESTION_IMPORT_BULK",
  ],
  QMS_DOCUMENT_CONTROL: [
    "QMS_ARCHIVE_DOCUMENTS",
    "QMS_UNARCHIVE_DOCUMENTS",
    "QMS_TRANSFER_OWNERSHIP",
  ],
};

/**
 * ONLY these permissions appear on Role Management and User Profile QMS card.
 * Shared With Me is not listed — it opens when Review or Approve is granted.
 * Quiz Manager / Document Control each grant their former granular children.
 */
export const QMS_ROLE_PROFILE_PERMISSIONS = [
  { key: "QMS_VAULT", label: "My Vault" },
  { key: "QMS_CAN_REVIEW_DOCUMENTS", label: "Review Document" },
  { key: "QMS_CAN_APPROVE_DOCUMENTS", label: "Approve Document" },
  { key: "QMS_PUBLISH_DOCUMENTS", label: "Publish Document" },
  { key: "QMS_DOCUMENT_LIBRARY", label: "Document Library" },
  { key: "QMS_LIBRARY_SEE_STAFF", label: "Assign/Unassign Library Staff" },
  { key: "QMS_LIBRARY_ADD_STAFF", label: "Assign/Unassign My Learning Staff" },
  { key: "QMS_QUIZ_MANAGER", label: "Quiz Manager" },
  { key: "QMS_DOCUMENT_CONTROL", label: "Document Control" },
];

export const QMS_ROLE_PROFILE_PERMISSION_KEYS = QMS_ROLE_PROFILE_PERMISSIONS.map(
  (item) => item.key,
);

export const QMS_ALL_PERMISSIONS = [
  ...QMS_PAGE_PERMISSIONS,
  ...QMS_ACTION_PERMISSIONS.map(({ key, label }) => ({ key, label })),
  ...QMS_GRANULAR_PERMISSIONS,
];

export const QMS_PERMISSION_GROUPS = [
  {
    id: "qms",
    title: "QMS",
    permissions: QMS_ROLE_PROFILE_PERMISSIONS,
  },
];

/** Legacy aliases so old DMS_* TargetView keys still grant the matching React action. */
export const QMS_ACTION_LEGACY_ALIASES = {
  QMS_VIEW_CHANGE_REQUEST: ["DMS_CHANGEREQUESTS"],
  QMS_CHANGE_STATUS: ["Change Status (Approve/Reject)"],
  QMS_CAN_REVIEW_DOCUMENTS: ["Can Review Documents"],
  QMS_CAN_APPROVE_DOCUMENTS: ["Can Approve Documents"],
  QMS_LIBRARY_VIEW_ASSIGNED_STAFF: ["View Assigned Staffs"],
  QMS_LIBRARY_AUDIT_SUMMARY: ["DMS_DOCUMENTLIBRARY_AUDITSUMMARY", "Audit Summary"],
  QMS_LIBRARY_SEE_STAFF: ["Can see library staff"],
  QMS_LIBRARY_ADD_STAFF: ["Add Staff"],
};

const LEGACY_QMS_ACTION_LABELS = Object.values(QMS_ACTION_LEGACY_ALIASES)
  .flat()
  .filter((value) => !String(value).startsWith("QMS_") && !String(value).startsWith("DMS_"));

export function isAssignableQmsPermissionKey(key) {
  const normalized = String(key || "").trim();
  if (!normalized) return false;
  if (normalized.startsWith("QMS_")) return true;
  if (normalized.startsWith("DMS_")) return true;
  if (LEGACY_QMS_ACTION_LABELS.includes(normalized)) return true;
  return false;
}

/** Keep non-QMS module keys; QMS keys limited to the role/profile card list. */
export function sanitizeSelectedPermissionsForSave(selectedPermissions = []) {
  const allowedQms = new Set(QMS_ROLE_PROFILE_PERMISSION_KEYS);
  const result = [];
  const seen = new Set();

  (selectedPermissions || []).forEach((raw) => {
    const key = String(raw || "").trim();
    if (!key || seen.has(key)) return;

    if (isAssignableQmsPermissionKey(key)) {
      if (allowedQms.has(key)) {
        result.push(key);
        seen.add(key);
      }
      return;
    }

    result.push(key);
    seen.add(key);
  });

  return result;
}
